// MenuButton.jsx

import React, { useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { formatCount } from 'utilities/Formatters';

// CSS
import './MenuButton.css';

// Icons
import Icon from 'svg/Icon';

/**
 * MenuButton
 * 
 * A menu button component, in glass style, that displays an icon.
 * 
 * @param {object} icon - The icon component to render inside the button.
 * @param {string} iconKey - Icon key to render via Icon.
 * @param {number} size - The size of the icon in pixels (default is 36).
 * @param {string} title - The title attribute for the button, used for tooltips.
 * @param {boolean} selected - Whether the button is currently selected.
 * @param {component} selectedIcon - Icon component to render when selected.
 * @param {string} selectedIconKey - Icon key to render via Icon when selected.
 * @param {'default'|'chrome'} [variant='default'] - Chrome is bordered squares (legacy).
 * @param {function} onClick - The function to call when the button is clicked.
 * 
 * @returns {ReactNode} MenuButton component.
 */
const MenuButton = ({
    icon: IconComponent,
    iconKey,
    path,
    size = 20,
    title,
    selected = false,
    selectedIcon: SelectedIconComponent,
    selectedColor,
    selectedIconKey,
    selectedPath,
    variant = 'default',
    onClick,
    count = 0,
}) => {

    // ----- CONTEXT ----- //

    // Theme Context

    const { theme } = useTheme();

    // ----- STATE ----- //

    // Hover State

    const [hovered, setHovered] = useState(false);

    const isChrome = variant === 'chrome';

    // ----- RENDER ----- //

    return (
        <button
          className={[
            'menu-button-container',
            isChrome ? 'menu-button-container--chrome' : '',
          ].filter(Boolean).join(' ')}
          style={{
                backgroundColor: isChrome
                    ? (hovered || selected ? theme.backgroundHover : theme.panel)
                    : (hovered ? theme.buttonHoverBackgroundColor : 'transparent'),
                borderColor: isChrome
                    ? (hovered || selected ? theme.foregroundFaded : theme.outlineMuted)
                    : undefined,
                color: isChrome
                    ? (hovered || selected ? theme.foreground : theme.foregroundMuted)
                    : undefined,
            }}
          type="button"
          title={title}
          onClick={onClick}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >

            {(() => {
                const useSelectedKey = selected && selectedIconKey;
                const useSelectedComponent = selected && SelectedIconComponent;
                const iconToUseKey = useSelectedKey ? selectedIconKey : iconKey;
                const iconToUsePath = selected && selectedPath ? selectedPath : path;
                const IconToUse = useSelectedComponent ? SelectedIconComponent : IconComponent;
                const iconColor = selected
                    ? selectedColor || theme.highlight
                    : (isChrome
                        ? (hovered ? theme.foreground : theme.foregroundMuted)
                        : (hovered ? theme.buttonHoverLabelColor : theme.foregroundMuted));

                return iconToUseKey || iconToUsePath ? (
                    <Icon
                      iconKey={iconToUseKey}
                      path={iconToUsePath}
                      color={iconColor}
                      height={size}
                      width={size}
                    />
                ) : (
                    <IconToUse
                      color={iconColor}
                      height={size}
                      width={size}
                    />
                );
            })()}

            {/* COUNT */}
            {count > 0 && (
                <div className="menu-button-count">
                    {formatCount(count)}
                </div>
            )}

        </button>
    );
};

// Dev Tools
MenuButton.displayName = 'MenuButton';

export default MenuButton;
